export const getElementFromEventTarget = (target: EventTarget | null): Element | null => {
  if (!target) {
    return null;
  }

  // If the target is a text node (e.g. `<div>Hello</div>`) return the parent `div` element
  if (target instanceof Text) {
    return target.parentElement;
  }

  if (target instanceof Element) {
    return target;
  }

  return null;
};

const hasInteractiveAttributes = (element: Element): boolean => {
  const tagName = element.tagName;

  if (
    tagName === 'BUTTON' ||
    tagName === 'INPUT' ||
    tagName === 'A' ||
    tagName === 'SELECT' ||
    tagName === 'TEXTAREA'
  ) {
    return true;
  }

  // This attribute typically decorates dropdowns and other interactive elements
  if (element.hasAttribute('data-interactive')) {
    return true;
  }

  // draggable=true elements are interactive and should be skipped
  if (element.getAttribute('draggable') === 'true') {
    return true;
  }

  const contentEditable = element.getAttribute('contenteditable');
  if (contentEditable === 'true' || contentEditable === '') {
    return true;
  }

  const role = element.getAttribute('role');
  if (role === 'button' || role === 'tab' || role === 'link') {
    return true;
  }

  return false;
};

/**
 * Determines if an event is targetting an interactive element.
 * This is used to determine if the user is currently interacting with a database
 * element, and therefore whether to forward the keyboard event to the parent.
 * @param target - The html element to check.
 * @returns True if the element is interactive, false otherwise.
 */
export const isInteractiveElement = (event: Event): boolean => {
  const target = getElementFromEventTarget(event.target);

  let current: Element | null = target;

  // Traverse up the DOM tree until we find an interactive element or reach the body
  while (current !== null && current !== document.body) {
    if (hasInteractiveAttributes(current)) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};
